import React, { useState } from 'react'
import { 
  XMarkIcon,
  EyeIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

interface ModerationImageViewerProps {
  currentImage?: string | null
  pendingImage?: string | null
  status: 'approved' | 'pending' | 'rejected'
  alt: string
  placeholder: string
  label: string
  className?: string
}

interface ImagePreviewProps {
  src: string | undefined
  alt: string
  placeholder: string
  label?: string
  isPending?: boolean
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ 
  src, 
  alt, 
  placeholder, 
  label,
  isPending = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [imageError, setImageError] = useState(false)

  if (!src || imageError) {
    return (
      <div className={`w-full h-32 rounded-lg flex items-center justify-center ${
        isPending ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-100'
      }`}>
        <div className="text-center">
          <UserIcon className={`w-8 h-8 mx-auto mb-2 ${
            isPending ? 'text-yellow-400' : 'text-gray-400'
          }`} />
          <p className={`text-sm ${
            isPending ? 'text-yellow-600' : 'text-gray-500'
          }`}>
            {placeholder}
          </p>
          {label && (
            <p className="text-xs text-gray-400 mt-1">{label}</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div 
        className="relative cursor-pointer group"
        onClick={() => setIsExpanded(true)}
      >
        <img
          src={src}
          alt={alt}
          className={`w-full h-32 object-cover rounded-lg transition-transform group-hover:scale-105 ${
            isPending ? 'border-2 border-yellow-300' : ''
          }`}
          onError={() => setImageError(true)}
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
          <EyeIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        {label && (
          <div className={`absolute bottom-1 left-1 px-2 py-1 rounded text-xs font-medium ${
            isPending 
              ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
              : 'bg-gray-100 text-gray-700'
          }`}>
            {label}
          </div>
        )}
      </div>

      {/* Expanded Image Modal */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setIsExpanded(false)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            {label && (
              <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-3 py-2 rounded-lg">
                {label}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export const ModerationImageViewer: React.FC<ModerationImageViewerProps> = ({
  currentImage,
  pendingImage,
  status,
  alt,
  placeholder,
  label,
  className = ''
}) => {
  const getStatusBadge = () => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Утверждено
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            На модерации
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            ✗ Отклонено
          </span>
        )
      default:
        return null
    }
  }

  const showComparison = status === 'pending' && currentImage && pendingImage

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Заголовок с статусом */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {getStatusBadge()}
      </div>

      {showComparison ? (
        // Упрощенное сравнение изображений
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-2">Текущее</div>
            <ImagePreview
              src={currentImage}
              alt={`${alt} (текущее)`}
              placeholder="Нет изображения"
              label="Текущее"
            />
          </div>
          
          <div>
            <div className="text-xs text-gray-500 mb-2">Предлагаемое</div>
            <ImagePreview
              src={pendingImage}
              alt={`${alt} (на модерации)`}
              placeholder="Нет изображения"
              label="На модерации"
              isPending={true}
            />
          </div>
        </div>
      ) : (
        // Показываем только одно изображение
        <div className="w-48">
          <ImagePreview
            src={(status === 'pending' && pendingImage) ? pendingImage : currentImage}
            alt={alt}
            placeholder={placeholder}
            isPending={status === 'pending' && !!pendingImage}
          />
        </div>
      )}
    </div>
  )
}
